import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconSearch } from '@tabler/icons-react';
import SearchInput from '../SearchInput';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import medicareApiService from '../../../services/medicare-api.service';
import commercialApiService from '../../../services/commercial-api.service';

import { useLoader } from '../../../loader/LoaderContext';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';

function Commercial({ updateDataToCoverageVerification }) {
  const user = useSelector((state) => state.auth.user);

  const formikRef = useRef();
  const { showLoader, hideLoader } = useLoader();

  const [selectedcountyCode, setSelectedCountyCode] = useState(null);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDrugTier, setSelectedDrugTier] = useState(null);
  const [selectedDrug, setSelectedDrug] = useState(null);

  const [fetchLoadIssuers, setFetchLoadIssuers] = useState(false);
  const [fetchLoadPlans, setFetchLoadPlans] = useState(false);
  const [fetchLoadDrugs, setFetchLoadDrugs] = useState(false);
  const [fetchLoadDrugTiers, setFetchLoadDrugTiers] = useState(false);

  const [drugSearchResultData, setDrugSearchResultData] = useState(null);

  const [loading, setLoading] = useState({
    zipCode: false,
    issuerName: false,
    planName: false,
    drugName: false,
    drugTier: false,
  });

  const validationSchema = Yup.object().shape({
    zipCode: Yup.string().required('Zip code is required'),
    zipCodeId: Yup.string().required('Please select a valid ZIP code'),
    issuerName: Yup.string().when('zipCodeId', {
      is: (val) => val && val.length > 0,
      then: () => Yup.string().required('Issuer name is required'),
    }),
    issuerNameId: Yup.string().when('zipCodeId', {
      is: (val) => val && val.length > 0,
      then: () => Yup.string().required('Please select a valid issuer'),
    }),
    planName: Yup.string().when('issuerNameId', {
      is: (val) => val && val.length > 0,
      then: () => Yup.string().required('Plan name is required'),
    }),
    planNameId: Yup.string().when('issuerNameId', {
      is: (val) => val && val.length > 0,
      then: () => Yup.string().required('Please select a valid plan'),
    }),
    drugTier: Yup.string().when('planNameId', {
      is: (val) => val && val.length > 0,
      then: () => Yup.string().required('Drug tier is required'),
    }),
    drugTierId: Yup.string().when('planNameId', {
      is: (val) => val && val.length > 0,
      then: () => Yup.string().required('Please select a valid drug tier'),
    }),
    drugName: Yup.string().when('drugTierId', {
      is: (val) => val && val.length > 0,
      then: () => Yup.string().required('Drug name is required'),
    }),
    drugNameId: Yup.string().when('drugTierId', {
      is: (val) => val && val.length > 0,
      then: () => Yup.string().required('Please select a valid drug'),
    }),
  });

  const searchZipCodes = async (zipCode) => {
    setSelectedCountyCode(null);
    setSelectedIssuer(null);
    setSelectedPlan(null);
    setDrugSearchResultData(null);
    setSelectedDrugTier(null);
    setSelectedDrug(null);
    setLoading((prev) => ({ ...prev, zipCode: true }));
    try {
      const zipSearchResults =
        await medicareApiService.zipCodeFuzzySearch(zipCode);

      const results = zipSearchResults.hits.hits.map((hit) => ({
        ...hit._source,
        value: hit._source.county_fips,
        label: hit._source.zip + ' - ' + hit._source.county_name,
      }));

      return results;
    } finally {
      setLoading((prev) => ({ ...prev, zipCode: false }));
    }
  };

  const searchIssuers = async (issuerName) => {
    setSelectedIssuer(null);
    setSelectedPlan(null);
    setDrugSearchResultData(null);
    setSelectedDrugTier(null);
    setSelectedDrug(null);
    setLoading((prev) => ({ ...prev, issuerName: true }));
    try {
      const issuerSearchResults =
        await commercialApiService.commercialProviderFuzzySearch(
          issuerName,
          selectedcountyCode.county_fips,
        );

      const results = issuerSearchResults.hits.hits.map((hit) => ({
        ...hit._source,
        value: hit._source.issuer_id,
        label: hit._source.issuer_name,
      }));

      return results;
    } finally {
      setLoading((prev) => ({ ...prev, issuerName: false }));
    }
  };

  const searchPlans = async (planName) => {
    setSelectedPlan(null);
    setDrugSearchResultData(null);
    setSelectedDrugTier(null);
    setSelectedDrug(null);
    setLoading((prev) => ({ ...prev, planName: true }));
    try {
      const planSearchResults =
        await commercialApiService.commercialPlanFuzzySearch(
          planName,
          selectedcountyCode.county_fips,
          selectedIssuer.issuer_id,
        );

      const results = planSearchResults.hits.hits.map((hit) => ({
        ...hit._source,
        value: hit._source.plan_id_standard_component,
        label: hit._source.plan_marketing_name,
      }));

      return results;
    } finally {
      setLoading((prev) => ({ ...prev, planName: false }));
    }
  };

  const searchDrugTiers = async (tierName) => {
    setSelectedDrug(null);
    setDrugSearchResultData(null);
    setLoading((prev) => ({ ...prev, drugName: true }));
    try {
      const drugSearchResults =
        await commercialApiService.commercialDrugFuzzySearch(
          tierName,
          selectedPlan.plan_id_standard_component,
        );
      setDrugSearchResultData(drugSearchResults);
      const results = drugSearchResults.unique_drug_tiers.map((drugTier) => ({
        ...drugTier,
        value: drugTier.tier,
        label: drugTier.tier === 'NULL' ? 'NOT SPECIFIED' : drugTier.tier,
      }));

      return results;
    } finally {
      setLoading((prev) => ({ ...prev, drugName: false }));
    }
  };

  const searchDrugs = async (drugName) => {
    setLoading((prev) => ({ ...prev, drugName: true }));
    try {
      const results = drugSearchResultData.hits.hits
        .filter((hit) => {
          return hit._source.drug_tier === selectedDrugTier.value;
        })
        .map((hit) => ({
          ...hit._source,
          document_id: hit._id,
          value: hit._source.drug_name,
          label: hit._source.drug_name,
        }));

      return results;
    } finally {
      setLoading((prev) => ({ ...prev, drugName: false }));
    }
  };

  const handleSelect = async (setFieldValue, field, item) => {
    switch (field) {
      case 'zipCode': {
        // Clear dependent fields and set new values in parallel
        await Promise.all([
          // Clear dependent fields
          setFieldValue('issuerName', ''),
          setFieldValue('issuerNameId', ''),
          setFieldValue('planName', ''),
          setFieldValue('planNameId', ''),
          setFieldValue('drugTier', ''),
          setFieldValue('drugTierId', ''),
          setFieldValue('drugName', ''),
          setFieldValue('drugNameId', ''),
          // Set new values
          setFieldValue(field, item.label),
          setFieldValue(`${field}Id`, item.value),
        ]);

        // Update component state after form values are set
        setSelectedIssuer(null);
        setSelectedPlan(null);
        setSelectedDrugTier(null);
        setSelectedDrug(null);
        setSelectedCountyCode(item);
        setFetchLoadIssuers(true);
        sendToKinesis('coverage_events', 'COMMERCIAL_INIT', {
          userid: user.userId,
        });
        break;
      }

      case 'issuerName': {
        // Clear dependent fields and set new values in parallel
        await Promise.all([
          // Clear dependent fields
          setFieldValue('planName', ''),
          setFieldValue('planNameId', ''),
          setFieldValue('drugTier', ''),
          setFieldValue('drugTierId', ''),
          setFieldValue('drugName', ''),
          setFieldValue('drugNameId', ''),
          // Set new values
          setFieldValue(field, item.label),
          setFieldValue(`${field}Id`, item.value),
        ]);

        // Update component state after form values are set
        setSelectedPlan(null);
        setSelectedDrugTier(null);
        setSelectedDrug(null);
        setFetchLoadPlans(true);
        setSelectedIssuer(item);
        break;
      }

      case 'planName': {
        // Clear dependent fields and set new values in parallel
        await Promise.all([
          // Clear dependent fields
          setFieldValue('drugTier', ''),
          setFieldValue('drugTierId', ''),
          setFieldValue('drugName', ''),
          setFieldValue('drugNameId', ''),
          // Set new values
          setFieldValue(field, item.label),
          setFieldValue(`${field}Id`, item.value),
        ]);

        // Update component state after form values are set
        setSelectedDrugTier(null);
        setSelectedDrug(null);
        setFetchLoadDrugTiers(true);
        setSelectedPlan(item);
        break;
      }

      case 'drugTier': {
        // Set new values in parallel
        await Promise.all([
          setFieldValue('drugName', ''),
          setFieldValue('drugNameId', ''),
          setFieldValue(field, item.label),
          setFieldValue(`${field}Id`, item.value),
        ]);

        // Update component state after form values are set
        setSelectedDrug(null);
        setFetchLoadDrugs(true);
        setSelectedDrugTier(item);

        break;
      }

      case 'drugName': {
        // Set new values in parallel
        await Promise.all([
          setFieldValue(field, item.label),
          setFieldValue(`${field}Id`, item.value),
        ]);

        // Update component state after form values are set
        setSelectedDrug(item);
        break;
      }

      default:
        console.warn(`Unhandled field: ${field}`);
        break;
    }
  };

  const handleTextChange = (setFieldValue, fieldName) => {
    switch (fieldName) {
      case 'zipCode':
        setFieldValue('issuerName', '');
        setFieldValue('issuerNameId', '');
        setFieldValue('planName', '');
        setFieldValue('planNameId', '');
        setFieldValue('drugTier', '');
        setFieldValue('drugTierId', '');
        setFieldValue('drugName', '');
        setFieldValue('drugNameId', '');
        setSelectedCountyCode(null);
        setSelectedIssuer(null);
        setSelectedPlan(null);
        setSelectedDrugTier(null);
        setSelectedDrug(null);
        break;
      case 'issuerName':
        setFieldValue('planName', '');
        setFieldValue('planNameId', '');
        setFieldValue('drugTier', '');
        setFieldValue('drugTierId', '');
        setFieldValue('drugName', '');
        setFieldValue('drugNameId', '');
        setSelectedIssuer(null);
        setSelectedPlan(null);
        setSelectedDrugTier(null);
        setSelectedDrug(null);
        break;
      case 'planName':
        setFieldValue('drugTier', '');
        setFieldValue('drugTierId', '');
        setFieldValue('drugName', '');
        setFieldValue('drugNameId', '');
        setSelectedPlan(null);
        setSelectedDrugTier(null);
        setSelectedDrug(null);
        break;
      case 'drugTier':
        setFieldValue('drugName', '');
        setFieldValue('drugNameId', '');
        setSelectedDrugTier(null);
        setSelectedDrug(null);
        break;
      case 'drugName':
        setFieldValue('drugNameId', '');
        setSelectedDrug(null);
        break;
      default:
        break;
    }
  };

  const handleFormSubmit = async () => {
    try {
      showLoader();
      const commercialDrugInfo =
        await commercialApiService.getFormularyDrugInfo(
          selectedPlan.plan_id_standard_component,
          selectedDrug.document_id,
        );

      const commercialPlanInfo =
        await commercialApiService.getCommercialPlanInfo(
          selectedPlan.plan_id_standard_component,
          selectedIssuer.issuer_id,
          selectedcountyCode.county_fips,
        );

      updateDataToCoverageVerification([
        commercialDrugInfo,
        commercialPlanInfo,
        selectedDrug?.drug_name,
      ]);

      sendToKinesis('coverage_events', 'COMMERCIAL_ACTIVITY', {
        searchterm: selectedDrug?.label,
        userid: user.userId,
        planname: selectedPlan?.label,
        stateid: selectedcountyCode?.state_id,
      });
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      hideLoader();
    }
  };
  return (
    <div className="search-form-padding">
      <h6>Enter Search Details</h6>
      <Formik
        innerRef={formikRef}
        initialValues={{
          zipCode: selectedcountyCode?.label ?? '',
          zipCodeId: selectedcountyCode?.value ?? '',
          issuerName: selectedIssuer?.label ?? '',
          issuerNameId: selectedIssuer?.value ?? '',
          planName: selectedPlan?.label ?? '',
          planNameId: selectedPlan?.value ?? '',
          drugTier: selectedDrugTier?.label ?? '',
          drugTierId: selectedDrugTier?.value ?? '',
          drugName: selectedDrug?.label ?? '',
          drugNameId: selectedDrug?.value ?? '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ errors, touched, setFieldValue, isSubmitting, isValid, dirty }) => (
          <Form className="cst-form">
            <div className="col-12 mb-xxl-3 mb-2">
              <label htmlFor="zipCode" className="form-label">
                ZIP Code
              </label>
              <Field name="zipCode">
                {({ field, form }) => (
                  <SearchInput
                    field={field}
                    form={form}
                    onSearch={searchZipCodes}
                    onSelect={(value) =>
                      handleSelect(setFieldValue, 'zipCode', value)
                    }
                    minSearchLength={1}
                    placeholder="Enter zip code"
                    disabled={false}
                    onTextChange={(fieldName) => {
                      handleTextChange(setFieldValue, fieldName);
                    }}
                    loading={loading.zipCode}
                  />
                )}
              </Field>
              {errors.zipCode && touched.zipCode && (
                <div className="text-danger small mt-1">{errors.zipCode}</div>
              )}
              {!errors.zipCode && errors.zipCodeId && touched.zipCodeId && (
                <div className="text-danger small mt-1">{errors.zipCodeId}</div>
              )}
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="issuerName" className="form-label">
                Issuer Name
              </label>
              <Field name="issuerName">
                {({ field, form }) => (
                  <SearchInput
                    field={field}
                    form={form}
                    onSearch={searchIssuers}
                    onSelect={(value) =>
                      handleSelect(setFieldValue, 'issuerName', value)
                    }
                    minSearchLength={0}
                    fetchAndFilter={fetchLoadIssuers}
                    fetchTrigger={selectedcountyCode?.value}
                    placeholder="Blue Cross Blue Shield of..."
                    disabled={!selectedcountyCode}
                    onTextChange={(fieldName) => {
                      handleTextChange(setFieldValue, fieldName);
                    }}
                    loading={loading.issuerName}
                  />
                )}
              </Field>
              {errors.issuerName && touched.issuerName && (
                <div className="text-danger small mt-1">
                  {errors.issuerName}
                </div>
              )}
              {!errors.issuerName &&
                errors.issuerNameId &&
                touched.issuerNameId && (
                  <div className="text-danger small mt-1">
                    {errors.issuerNameId}
                  </div>
                )}
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="planName" className="form-label">
                Plan Name
              </label>
              <Field name="planName">
                {({ field, form }) => (
                  <SearchInput
                    field={field}
                    form={form}
                    onSearch={searchPlans}
                    onSelect={(value) =>
                      handleSelect(setFieldValue, 'planName', value)
                    }
                    minSearchLength={0}
                    fetchAndFilter={fetchLoadPlans}
                    fetchTrigger={selectedIssuer?.value}
                    placeholder="MyBlue Health..."
                    disabled={!selectedIssuer}
                    onTextChange={(fieldName) => {
                      handleTextChange(setFieldValue, fieldName);
                    }}
                    loading={loading.planName}
                  />
                )}
              </Field>
              {errors.planName && touched.planName && (
                <div className="text-danger small mt-1">{errors.planName}</div>
              )}
              {!errors.planName && errors.planNameId && touched.planNameId && (
                <div className="text-danger small mt-1">
                  {errors.planNameId}
                </div>
              )}
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="drugTier" className="form-label">
                Drug Tier
              </label>
              <Field name="drugTier">
                {({ field, form }) => (
                  <SearchInput
                    isDropdown={true}
                    field={field}
                    form={form}
                    onSearch={searchDrugTiers}
                    onSelect={(value) =>
                      handleSelect(setFieldValue, 'drugTier', value)
                    }
                    minSearchLength={0}
                    fetchAndFilter={fetchLoadDrugTiers}
                    fetchTrigger={selectedPlan?.value}
                    placeholder="Select Drug Tier"
                    disabled={!selectedPlan}
                    onTextChange={(fieldName) => {
                      handleTextChange(setFieldValue, fieldName);
                    }}
                    loading={loading.drugTier}
                  />
                )}
              </Field>
              {errors.drugTier && touched.drugTier && (
                <div className="text-danger small mt-1">{errors.drugTier}</div>
              )}
              {!errors.drugTier && errors.drugTierId && touched.drugTierId && (
                <div className="text-danger small mt-1">
                  {errors.drugTierId}
                </div>
              )}
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="drugName" className="form-label">
                Drug Name
              </label>
              <Field name="drugName">
                {({ field, form }) => (
                  <SearchInput
                    field={field}
                    form={form}
                    onSearch={searchDrugs}
                    onSelect={(value) =>
                      handleSelect(setFieldValue, 'drugName', value)
                    }
                    minSearchLength={0}
                    fetchAndFilter={fetchLoadDrugs}
                    fetchTrigger={selectedDrugTier?.value}
                    placeholder="Atorvastatin..."
                    disabled={!selectedDrugTier}
                    onTextChange={(fieldName) => {
                      handleTextChange(setFieldValue, fieldName);
                    }}
                    loading={loading.drugName}
                  />
                )}
              </Field>
              {errors.drugName && touched.drugName && (
                <div className="text-danger small mt-1">{errors.drugName}</div>
              )}
              {!errors.drugName && errors.drugNameId && touched.drugNameId && (
                <div className="text-danger small mt-1">
                  {errors.drugNameId}
                </div>
              )}
            </div>
            <div className="col-12 text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary px-4"
                disabled={isSubmitting || !(isValid && dirty)}
              >
                <IconSearch
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-search"
                />
                Search
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
Commercial.propTypes = {
  updateDataToCoverageVerification: PropTypes.func.isRequired,
};
export default Commercial;
